import React, { useState, useMemo } from 'react';
import { MoreHorizontal, Eye, Edit, Key, Mail, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatToCurrency } from '../../utils/common';
import {
  UserAvatar, ConfirmModal, ActionMenu, ActionMenuItem, ActionMenuDivider,
  StatusBadge, AppDataTable,
} from '../ui';
import { useBranch } from '../../contexts/BranchContext';

/**
 * StudentList — the students table.
 *
 * Moved from `Common/DataTable` to `ui/AppDataTable` (2026-09-03), which is the
 * app's one table. The selection, the per-row class and the rows-per-page select
 * this page needs were added to `AppDataTable` and `Paginator` in the same
 * change rather than kept as a second implementation.
 *
 * ⚠️ **No column filters and no sort, deliberately.** The list is SERVER
 * paginated — `StudentsPage` fetches one page from `/students/list` — so a
 * funnel here would narrow ten rows and say nothing about the other four
 * thousand, and a sort arrow would reorder the ten on screen rather than the
 * list. The filters that DO work live in `StudentsPage`, because the endpoint
 * accepts them (`course`, `batch`, `noBatch`, `search`, `status`).
 *
 * Moving those into the header is a real improvement and a real piece of work:
 * `columnFilters` / `onColumnFilterChange` are controlled precisely so they can
 * be sent upstream, but each one needs a server parameter behind it. See
 * `docs/design-system/TABLE_MIGRATION.md`.
 */

/* One empty marker for the whole table. Four cells wrote a bare `-` inline —
   un-muted, and a different glyph from this one — so "no value" read as two
   different things in the same row. */
const DASH = <span className="tw-text-content-subtle">—</span>;

const StudentList = ({
  students = [],
  total = 0,
  page = 1,
  setPage = () => {},
  selected = [],
  setSelected,
  selectable = true,
  canEdit = false,
  pageSize = 10,
  setPageSize,
  onDelete,
  resetStudentPassword,
  resendWelcomeEmail,
  parentPage,
  /* The contract students module: each row says where its evaluation stands. */
  showEvaluation = false,
  loading = false,
  /* ⚠️ Defaults FALSE: outside a bounded `app-page-fill` column the table has
     no height of its own and must flow with the page. Only `/students` is such
     a column, and it passes true. */
  fill = false,
}) => {
  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { isMultiBranch, branches } = useBranch();

  const columns = useMemo(() => [
    {
      key: 'student',
      label: 'Student',
      width: '25%',
      render: (student) => (
        <div className="tw-flex tw-items-center tw-gap-3">
          <UserAvatar name={student.name} size="sm" />
          <div className="tw-min-w-0">
            <a
              href={`/students/${student._id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/students/${student._id}`);
              }}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-font-semibold tw-text-content tw-no-underline hover:tw-text-primary"
            >
              {student.name}
              {/* The dot is a state, so it carries a name as well as a colour —
                  it used to be a bare "●" that announced as a bullet. */}
              {student.status === 'active' && student.accountCreated && (
                <span className="tw-text-success" title="Account active" aria-label="Account active">●</span>
              )}
              {student.status === 'inactive' && (
                <span className="tw-text-content-subtle" title="Inactive" aria-label="Inactive">●</span>
              )}
            </a>
            <div className="tw-truncate tw-text-[0.75rem] tw-tabular-nums tw-text-content-subtle">{student.mobile || '—'}</div>
            <div className="tw-truncate tw-text-[0.75rem] tw-text-content-subtle">{student.email}</div>
            {/* In this cell, not a column of its own: the table is already as wide
                as a 1440px screen, so a ninth column lands past the edge. */}
            {showEvaluation && student.evaluationStatus && (
              <a
                href={`/students/${student._id}?tab=evaluation`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/students/${student._id}?tab=evaluation`);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                className="tw-mt-1 tw-inline-block tw-no-underline"
              >
                <StatusBadge status={student.evaluationStatus} domain="studentEvaluation" />
              </a>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'course',
      label: 'Course',
      width: '20%',
      render: (student) => {
        const additional = (student.courseAssignments || []).filter(
          (a) => !a.isPrimary && a.status === 'active'
        );
        return (
          <div>
            <div className="tw-font-medium">{student.course?.name || '—'}</div>
            <div className="tw-text-[0.75rem] tw-text-content-subtle">{student.batch?.name || '—'}</div>
            {additional.length > 0 && (
              <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-1">
                {additional.map((a, i) => (
                  <StatusBadge key={a._id || i} status="neutral">
                    {a.courseId?.name || 'Course'}
                    {a.batchId?.name ? ` · ${a.batchId.name}` : ''}
                  </StatusBadge>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    ...(isMultiBranch && branches.length > 1 ? [{
      key: 'branch',
      label: 'Branch',
      width: '12%',
      // The student's own home branch. Falls back to the batch's branch for
      // rows written before the home-branch backfill ran.
      render: (student) => (
        <span className="tw-text-[0.75rem] tw-text-content-subtle">
          {student.branchId?.name || student.batch?.branchId?.name || '—'}
        </span>
      ),
    }] : []),
    ...(parentPage === 'students' ? [{
      key: 'fees',
      label: 'Fees',
      width: '15%',
      align: 'right',
      render: (student) => (
        <div>
          <div className="tw-font-medium tw-tabular-nums">{formatToCurrency(student.totalFees || 0)}</div>
          <div className="tw-text-[0.75rem] tw-tabular-nums tw-text-content-subtle">
            Pending: {formatToCurrency(student.pendingFees || 0)}
          </div>
        </div>
      ),
    }] : []),
    {
      key: 'aiAttempts',
      label: 'AI Attempts',
      width: '12%',
      align: 'right',
      render: (student) => (student.totalAIAttempts
        ? <span className="tw-tabular-nums">{student.totalAIAttempts}</span>
        : DASH),
    },
    {
      key: 'assignments',
      label: 'Assignment Sessions',
      width: '15%',
      align: 'right',
      render: (student) => (student.totalAssignmentSessions
        ? <span className="tw-tabular-nums">{student.totalAssignmentSessions}</span>
        : DASH),
    },
    {
      key: 'joinedDate',
      label: 'Joined Date',
      width: '12%',
      /* A secondary column, and a column of numbers: muted ink and
         `tabular-nums`, so the dates line up down the row instead of shifting
         with each digit's width. */
      render: (student) => (student.createdAt
        ? (
          <span className="tw-tabular-nums tw-text-content-muted">
            {new Date(student.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
          </span>
        )
        : DASH),
    },
    {
      key: 'status',
      label: 'Status',
      width: '10%',
      render: (student) => (
        <StatusBadge status={student.status === 'inactive' ? 'inactive' : 'active'}>
          {student.status === 'inactive' ? 'Inactive' : 'Active'}
        </StatusBadge>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 48,
      pinned: 'right',
      resizable: false,
      // ⚠️ The row is clickable, so every control in this cell must stop the
      // click reaching it — otherwise opening the menu also navigates away.
      render: (student) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for ${student.name}`} trigger={<MoreHorizontal size={16} />}>
            <ActionMenuItem onClick={() => navigate(`/students/${student._id}`)}>
              <Eye size={14} /> View Details
            </ActionMenuItem>
            {canEdit && (
              <ActionMenuItem onClick={() => navigate(`/students/edit/${student._id}`)}>
                <Edit size={14} /> Edit
              </ActionMenuItem>
            )}
            {student.accountCreated && resetStudentPassword && (
              <ActionMenuItem onClick={() => resetStudentPassword(student)}>
                <Key size={14} /> Reset password
              </ActionMenuItem>
            )}
            {!student.accountCreated && resendWelcomeEmail && (
              <ActionMenuItem onClick={() => resendWelcomeEmail(student)}>
                <Mail size={14} /> Resend Welcome Email
              </ActionMenuItem>
            )}
            {onDelete && (
              <>
                <ActionMenuDivider />
                <ActionMenuItem danger onClick={() => setDeleteTarget(student)}>
                  <Trash2 size={14} /> Delete
                </ActionMenuItem>
              </>
            )}
          </ActionMenu>
        </div>
      ),
    },
  ], [isMultiBranch, branches.length, parentPage, showEvaluation, navigate, canEdit, onDelete, resetStudentPassword, resendWelcomeEmail]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <AppDataTable
        fill={fill}
        resizable
        storageKey="students-list"
        columns={columns}
        rows={students}
        rowKey={(s) => s._id}
        loading={loading}
        selectable={selectable}
        selectedRows={selected}
        onSelectionChange={setSelected}
        /* An inactive student's row keeps the danger tint AND the 3px red edge
           it had under `.student-row--inactive`, which was more than a dimmed
           row — it is the signal an admin scans the list for.

           ⚠️ The edge is an inset shadow on the first cell, not `border-l` on
           the `<tr>`: this table is `border-separate`, and a border on a row
           element does not paint under that model. */
        rowClassName={(row) => (row.status === 'inactive'
          ? '!tw-bg-danger/[0.06] [&>td:first-child]:tw-shadow-[inset_3px_0_0_hsl(var(--danger))] [&_td]:tw-opacity-75'
          : '')}
        onRowClick={(s) => navigate(`/students/${s._id}`)}
        emptyMessage="No students found"
        pagination={{
          page,
          totalPages,
          total,
          pageSize,
          onChange: setPage,
          infoLabel: 'students',
          ...(setPageSize && {
            pageSizeOptions: [10, 25, 50, 100],
            // ⚠️ Back to page 1: page 7 of 10-per-page does not exist at 100.
            onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
          }),
        }}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={() => { onDelete(deleteTarget._id); setDeleteTarget(null); }}
        title="Delete Student"
        message={`Are you sure you want to delete ${deleteTarget?.name}?`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

export default StudentList;
